import React from 'react';
import * as yup from 'yup';
import { StyleProps } from '$/components/ui';
import {
  InVideoRegistrationDetails,
  InteractionDetails,
  InteractionOverlaySettings,
  UngatedSessionType,
} from '$/GraphQL/schema/types';

import { AnalyticsCardProps } from '../cards/types';
import {
  InteractionInfo,
  InteractionModalFooterProps,
  NoBodyCard,
  OverlayComponentProps,
  OverlayDimensions,
  VideoOverlayTypes,
} from '$/components/Interactions/InteractionInfo';
import type { InteractionModalTitleProps } from '../types';
import {
  ModalValidationRules,
  InteractionModalComponentProps,
  InteractionModalAccordionsHook,
  InteractionAccordionPanelProps,
} from '../types';
import { OverlayInteractionInfo } from '../OverlayInteractionInfo';

import { InVideoRegistrationIcon } from '$/images/FontAwesomeIcons';
import isMobile from '$/utils/isMobile';
import { theme } from 'twin.macro';
import { isValidMaxCharsMarkdown } from '$/utils/string';
import InVideoRegistrationOverlay from './InVideoRegistrationCard';
import { useInVideoRegistrationCalcDimensions } from './useInVideoRegistrationCalcDimensions';
import InVideoRegistrationOverlayCard from './InVideoRegistrationOverlayCard';
import Form from '$/components/ui/Form';

const AnalyticsClickCard = React.lazy(() => import('../cards/AnalyticsClickCard'));
const InVideoRegistrationSettings = React.lazy(() => import('./InVideoRegistrationSettings'));

const MAX_TITLE_CHARACTERS = 180;

const InVideoRegistrationSettingsAccordion: (
  props: InteractionModalComponentProps
) => InteractionAccordionPanelProps[] = ({ interaction }) => {
  return [
    {
      key: 'Settings',
      active: true,
      title: { content: <Form.AccordionTitle title={'Settings'} /> },
      content: { content: <InVideoRegistrationSettings interaction={interaction} /> },
    },
  ];
};

export class InVideoRegistrationInfo extends OverlayInteractionInfo {
  public typeName(): string | undefined {
    return `In-video registration`;
  }

  public color(): string | undefined {
    return theme`colors.orange.400`.toString();
  }

  public detailsKey(): string | undefined {
    return 'inVideoRegistration';
  }

  public description(): string {
    return this.i.details?.description || this.typeName() || '';
  }

  public detailsDefault(interactions: InteractionInfo[]): InteractionDetails {
    const defaults = super.detailsDefault(interactions);
    const inVideoRegistration: InVideoRegistrationDetails = defaults?.inVideoRegistration ?? {
      displayIn: [UngatedSessionType.Replay, UngatedSessionType.OnDemand],
      isRequired: true,
      showOnlyBuiltInFields: true,
      hideConsentCheckbox: false,
      buttonColor: this.ewebinar?.actionColor || '#39A1B2',
    };

    return {
      ...defaults,
      inVideoRegistration,
    };
  }

  public renderOverlayComponent(): React.FC<OverlayComponentProps> {
    return InVideoRegistrationOverlay;
  }

  public userCanAdd(): boolean {
    return true;
  }

  public category(): 'Standard' | 'Special' | 'Anchor' | 'Conversion' {
    return 'Special';
  }

  public appearAtBeforeWatchingTitle(): string | null {
    return 'Surface registration form';
  }

  public hasExpiresInField(): boolean {
    return false;
  }

  public hasShowTimerToggle(): boolean {
    return false;
  }

  public hasConditionalDisplay(): boolean {
    return false;
  }

  public supportsShortcut(): boolean {
    return false;
  }

  public onlyOneInstance(): boolean {
    return true;
  }

  public hasResultsAppearAtField(): boolean {
    return false;
  }

  public hasPropertyNameField(): boolean {
    return false;
  }

  public hasPinToTopToggle(): boolean {
    return false;
  }

  public isInteractiveCard(): boolean {
    return true;
  }

  public hasEndsAt(): boolean {
    return false;
  }

  public modalValidationRules(): ModalValidationRules {
    return {
      title: yup.string().test('maxCharsMessage', function (value: string | undefined) {
        return isValidMaxCharsMarkdown(MAX_TITLE_CHARACTERS, value);
      }),
      inVideoRegistration: yup.object().shape({
        displayIn: yup.array(yup.string()).required(),
        isRequired: yup.boolean().required(),
        showOnlyBuiltInFields: yup.boolean().required(),
        hideConsentCheckbox: yup.boolean().nullable().notRequired(),
        title: yup.string().nullable().notRequired(),
        buttonText: yup.string().nullable().notRequired(),
      }),
    };
  }

  public attendeeCardValidationRules(): yup.AnyObjectSchema | undefined {
    return undefined;
  }

  public icon(): React.FC<StyleProps> {
    return InVideoRegistrationIcon;
  }

  public isInteractiveOverlay(): boolean {
    return true;
  }

  public staticOverlayPosition(): boolean {
    return true;
  }

  public hasCard(_isPreview?: boolean): boolean {
    return false;
  }

  public hasVideoOverlay(): VideoOverlayTypes | null {
    return isMobile ? 'over-area' : 'over-video';
  }

  public hasAnswers(): boolean {
    return true;
  }

  public useModalPreviewComponent(): React.LazyExoticComponent<
    React.FC<InteractionModalComponentProps>
  > | null {
    return null;
  }

  public modalTitleComponent(): React.FC<InteractionModalTitleProps> | null {
    return null;
  }

  public modalSettingsAccordions(): InteractionModalAccordionsHook | null {
    return InVideoRegistrationSettingsAccordion;
  }

  public modalFooterComponent(): React.FC<InteractionModalFooterProps> | null {
    return null;
  }

  public analyticsCardComponent(): React.LazyExoticComponent<React.FC<AnalyticsCardProps>> {
    return AnalyticsClickCard;
  }

  public cardBodyComponent(): React.FC {
    return NoBodyCard;
  }

  public cardComponent(): React.FC<{ onClickHeader?: (() => void) | undefined }> {
    return InVideoRegistrationOverlayCard;
  }

  public calcOverlayDimensionsHook(): (
    interaction: InteractionInfo,
    playerWidth: number,
    playerHeight: number,
    settings?: InteractionOverlaySettings | null
  ) => OverlayDimensions {
    return useInVideoRegistrationCalcDimensions;
  }

  public hasResultsToShow(): boolean {
    return false;
  }

  public onlyExistsInPresentationRoom(): boolean {
    return true;
  }
}
